import { ParkingPass } from '@m/types/api/ParkingPass';
import {
  PassModificationPreviewResponse,
  PassPlanPreview,
  PassReceiptResponse,
} from '@m/types/api/pass/SelfServiceParkingPassPlan';
import { GrpcCurrency, GrpcTimestamp } from '@m/types/grpc/GrpcTypes';
import { apiFetch } from '@m/utils/http';

export const SelfServiceParkingPassService = {
  getPasses: ({ from }: { from: GrpcTimestamp }) =>
    apiFetch<{ parkingPasses: ParkingPass[] }>('/customer/passes', { params: { from } }),

  getPass: ({ passUuid }: { passUuid: string }) =>
    apiFetch<{ parkingPass: ParkingPass }>(`/customer/passes/${passUuid}`),

  previewPassPlans: ({
    siteUuid,
    requestedConfigurations,
    discount,
  }: {
    siteUuid: string;
    requestedConfigurations: {
      [index: number]: {
        from: string;
        to: string;
      };
    };
    discount?: {
      validationQrUuid: string;
    };
  }) =>
    apiFetch<{ passPlanConfigurations: Record<number, PassPlanPreview> }>(
      `/v2/sites/${siteUuid}/pass-plans/preview`,
      {
        method: 'POST',
        body: {
          zoneId: siteUuid,
          requestedConfigurations,
          discount,
        },
      },
    ),

  purchasePassPlan: ({
    visitUuid,
    planUuid,
    plateText,
    plateState,
    from,
    to,
    customerId,
    paymentMethodId,
    discount,
  }: {
    visitUuid: string;
    planUuid: string;
    plateText: string;
    plateState: string;
    from: GrpcTimestamp;
    to: GrpcTimestamp;
    customerId: number;
    paymentMethodId: number;
    discount?: {
      validationQrUuid: string;
    };
  }) =>
    apiFetch<{ passUuid: string; invoiceUuids: string[] }>(
      `/customer/visit/${visitUuid}/parking-pass/purchase`,
      {
        method: 'POST',
        body: {
          coverage: {
            licensePlate: {
              licensePlateText: plateText,
              licensePlateState: plateState,
            },
          },
          planUuid,
          from,
          to,
          customerId,
          paymentMethodId,
          discount,
        },
      },
    ),

  previewParkingPassModifications: ({
    visitUuid,
    passUuid,
    options,
  }: {
    visitUuid: string;
    passUuid: string;
    options?: {
      from: GrpcTimestamp;
      to: GrpcTimestamp;
    };
  }) =>
    apiFetch<PassModificationPreviewResponse>(
      `/customer/visit/${visitUuid}/parking-pass/${passUuid}/preview-modifications`,
      {
        params: options ? { from: options.from, to: options.to } : undefined,
      },
    ),

  executeParkingPassModification: ({
    visitUuid,
    passUuid,
    to,
    paymentMethodId,
    customerId,
    passPrice,
  }: {
    visitUuid: string;
    passUuid: string;
    to: string; // ISO
    paymentMethodId: number;
    customerId: number;
    passPrice: GrpcCurrency;
  }) =>
    apiFetch<{ invoiceUuids: string[] }>(
      `/customer/visit/${visitUuid}/parking-pass/${passUuid}/modify`,
      {
        method: 'POST',
        body: {
          passUuid,
          to,
          paymentMethodId,
          customerId,
          passPrice,
        },
      },
    ),

  getParkingPassReceipt: ({ passUuid }: { passUuid: string }) =>
    apiFetch<PassReceiptResponse>(`/customer/passes/${passUuid}/receipt`),

  getPassValidationUUID: ({ signCode }: { signCode: string }) =>
    apiFetch<{ validationQrUUID: string }>(`/customer/validation-code`, {
      method: 'POST',
      body: { signCode },
    }),

  deleteValidationCode: () =>
    // TODO: Update type params once we know the response type for this endpoint
    apiFetch('/customer/validation', {
      method: 'DELETE',
    }),
};
